import React, { useCallback, useState } from 'react';
import { ImageBackground } from 'expo-image';
import { useRouter } from 'expo-router';
import { defineMessages, useIntl, type MessageValue, type NoMessageValues } from 'react-intl';
import { ScrollView, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { theme, TYPE_CONFIG } from '@garnish/constants';
import { BodyText, Button, LabelText, TitleText, useResponsive } from '@garnish/core';
import { formatDate } from '@garnish/forms';
import { Modal } from '@garnish/overlays';

import { ModalBgSM, ModalBgXS } from './assets';
import { useChallengeCompletionTelemetry } from './hooks';

// ─────────────────────────────────────────────────────────────────────────

export const ChallengeCompletionModal = (props: ChallengeCompletionModalProps) => {
  const { challengeCompletionDetails } = props;

  const { title, celebrationCopy: description } = challengeCompletionDetails;

  // ─── State ───────────────────────────────────────────────────────────

  const [isModalVisible, setIsModalVisible] = useState(true);

  // ─────────────────────────────────────────────────────────────────────

  const { formatMessage } = useIntl();
  const router = useRouter();
  const { match } = useResponsive();

  const {
    trackNavigateToLoyaltyTab,
    trackChallengeCompletionModalShow,
    trackChallengeCompletionModalDismiss,
  } = useChallengeCompletionTelemetry();

  // ─── Derived data ────────────────────────────────────────────────────

  const expirationDate = challengeCompletionDetails.expirationDate
    ? new Date(challengeCompletionDetails.expirationDate)
    : null;
  const formattedExpirationDate = formatDate(expirationDate);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const navigateToLoyaltyTab = useCallback(() => {
    trackNavigateToLoyaltyTab();

    if (router.canDismiss()) {
      router.dismissAll();
    }

    router.navigate('/loyalty');
  }, [router, trackNavigateToLoyaltyTab]);

  const dismissChallengeCompletionModal = useCallback(() => {
    setIsModalVisible(false);
  }, []);

  const handleNavigateToLoyaltyTab = useCallback(() => {
    dismissChallengeCompletionModal();
    navigateToLoyaltyTab();
  }, [dismissChallengeCompletionModal, navigateToLoyaltyTab]);

  const onChallengeCompletionModalShown = useCallback(() => {
    trackChallengeCompletionModalShow(challengeCompletionDetails);
  }, [challengeCompletionDetails, trackChallengeCompletionModalShow]);

  const onChallengeCompletionModalDismissed = useCallback(() => {
    trackChallengeCompletionModalDismiss();
  }, [trackChallengeCompletionModalDismiss]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const safeAreaInsets = useSafeAreaInsets();
  const containerSafeInsetStyle = useStyle(
    () => ({
      paddingTop: safeAreaInsets.top,
      paddingBottom: safeAreaInsets.bottom,
    }),
    [safeAreaInsets],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Modal
      size={match(['full', 'small'])}
      fitHeight
      animationType="fade"
      accessibilityLabel={formatMessage(messages.challengesRewardsCelebrationA11yTitle)}
      visible={isModalVisible}
      onRequestClose={dismissChallengeCompletionModal}
      onShown={onChallengeCompletionModalShown}
      onDismissed={onChallengeCompletionModalDismissed}
      withSafeAreaView={false}
    >
      <ImageBackground
        source={match([ModalBgXS, ModalBgSM])}
        contentFit="cover"
        style={[styles.container, containerSafeInsetStyle]}
      >
        <Modal.CloseBtn palette="neutral" onPress={dismissChallengeCompletionModal} />

        <ScrollView
          contentContainerStyle={match([styles.contentContainerXS, styles.contentContainerSM])}
        >
          <Modal.Row style={styles.contentInnerContainer}>
            {title ? (
              <TitleText sizeMatch={['40', '64', '80']} style={[styles.text, styles.title]}>
                {title}
              </TitleText>
            ) : null}

            {formattedExpirationDate ? (
              <LabelText sizeMatch={['12', '14']} style={[styles.text, styles.expirationDate]}>
                {formatMessage(messages.challengeCompletionUseBy, {
                  date: formattedExpirationDate,
                })}
              </LabelText>
            ) : null}

            {description ? (
              <BodyText sizeMatch={['14', '16']} style={styles.text}>
                {description}
              </BodyText>
            ) : null}
          </Modal.Row>
        </ScrollView>

        <Modal.Footer withoutTopBorder style={match([styles.footerXS, styles.footerSM])}>
          <Button
            palette="primary"
            style={styles.cta}
            onPress={handleNavigateToLoyaltyTab}
            size="large"
          >
            {formatMessage(messages.challengesRewardsSeeMyRewards)}
          </Button>
        </Modal.Footer>
      </ImageBackground>
    </Modal>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly challengesRewardsCelebrationA11yTitle: NoMessageValues;
  readonly challengeCompletionUseBy: { readonly date: MessageValue };
  readonly challengesRewardsSeeMyRewards: NoMessageValues;
}>({
  challengesRewardsCelebrationA11yTitle: {
    defaultMessage: 'New reward',
    description: 'Challenges | Rewards | Celebration | A11y Title',
  },
  challengeCompletionUseBy: {
    defaultMessage: 'Use by {date}',
    description: 'Challenge Completion | Use By',
  },
  challengesRewardsSeeMyRewards: {
    defaultMessage: 'See my rewards',
    description: 'Challenges | Rewards | See My Rewards',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
  },
  contentContainerXS: {
    flex: 1,
    paddingTop: theme.spacing['10'],
  },
  contentContainerSM: {
    flex: 1,
    paddingTop: theme.spacing['20'],
  },
  contentInnerContainer: {
    flex: 1,
  },
  title: {
    ...TYPE_CONFIG.TITLE['48'],
    marginTop: 'auto',
  },
  text: {
    color: theme.colors.KALE,
    marginBottom: theme.spacing['4'],
  },
  expirationDate: {
    textTransform: 'uppercase',
  },
  cta: {
    borderWidth: 0,
  },
  footerXS: {
    marginTop: theme.spacing['6'],
  },
  footerSM: {
    marginTop: theme.spacing['6'],
    marginBottom: theme.spacing['4'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ChallengeCompletionModalProps = Readonly<{
  challengeCompletionDetails: {
    celebrationCopy: string;
    challengesIds: readonly string[];
    expirationDate?: string | null;
    rewardUsageDisclaimer?: string | null;
    title: string | null;
  };
}>;
